import {
  isMoveSubTrackRejectionReason,
  type MoveSubTrackRejectionReason,
} from '@strength/domain';

/**
 * A track mutation the server refused.
 *
 * `rejectionReason` is set only when the server named one *and* this build has
 * the reason in its union — a client older than the server falls back rather
 * than mis-reading. Callers that have no copy for a reason use `message`.
 */
export class TrackMutationError extends Error {
  readonly rejectionReason: MoveSubTrackRejectionReason | undefined;

  constructor(message: string, rejectionReason?: MoveSubTrackRejectionReason) {
    super(message);
    this.rejectionReason = rejectionReason;
  }
}

/**
 * The reason out of a Zero error envelope. Zero hands `details` through
 * untouched from whatever the mutator threw, which is where the service put it.
 */
export function zeroRejectionReason(error: {
  details?: unknown;
}): MoveSubTrackRejectionReason | undefined {
  const { details } = error;
  if (!details || typeof details !== 'object' || Array.isArray(details)) {
    return undefined;
  }

  return asRejectionReason((details as { reason?: unknown }).reason);
}

/**
 * The same reason off a tRPC client error, where the error formatter parks it
 * under `shape.data`.
 */
export function trpcRejectionReason(
  error: unknown,
): MoveSubTrackRejectionReason | undefined {
  const { data } = error as { data?: unknown };
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return undefined;
  }

  const { rejection } = data as { rejection?: unknown };
  if (!rejection || typeof rejection !== 'object' || Array.isArray(rejection)) {
    return undefined;
  }

  return asRejectionReason((rejection as { reason?: unknown }).reason);
}

function asRejectionReason(
  value: unknown,
): MoveSubTrackRejectionReason | undefined {
  return isMoveSubTrackRejectionReason(value) ? value : undefined;
}
